import React, { useState, useEffect } from 'react';
import apiService from '../config';

const HighwaySection93 = () => {
  const [rows, setRows] = useState([]);
  const [projectId, setProjectId] = useState(null);
  const [proposalId, setProposalId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editedFields, setEditedFields] = useState({}); // Track edited fields per row as an array

  // Fetch projectId and proposalId from localStorage
  useEffect(() => {
    const getProjectInfo = () => {
      const currentProjectData = localStorage.getItem('currentProjectData');
      if (currentProjectData) {
        try {
          const projectData = JSON.parse(currentProjectData);
          setProjectId(projectData.project_id || projectData.id);
        } catch (e) {
          console.error('Error parsing current project data:', e);
        }
      }

      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const user = JSON.parse(userData);
          setProposalId(user.data?.default_proposal || user.default_proposal || null);
        } catch (e) {
          console.error('Error parsing user data:', e);
        }
      }
    };

    getProjectInfo();
  }, []);

  // Fetch initial rows data using apiService
  useEffect(() => {
    if (projectId && proposalId) {
      const fetchData = async () => {
        setLoading(true);
        try {
          const response = await apiService.get("getAllOwner", {
            projectId: projectId,
            proposalId: proposalId,
            isActive: 1
          });

          const mappedRows = response.landAcquisition?.flatMap(acquisition =>
            acquisition.owners.map(owner => ({
              id: owner.land_owner_id,
              surveyNo: acquisition.survey_no || '',
              vargikaran: 'No',
              kshetra: owner.owner_aqr_area || '',
              bhudharanBadal: owner.owner_name || '',
              kshetraNew: '',
              bhudharanBadalNew: ''
            }))
          ) || [];

          setRows(mappedRows.length > 0 ? mappedRows : [{
            id: 1,
            surveyNo: '',
            vargikaran: 'No',
            kshetra: '',
            bhudharanBadal: '',
            kshetraNew: '',
            bhudharanBadalNew: ''
          }]);
        } catch (err) {
          console.error('Error fetching data:', err);
        } finally {
          setLoading(false);
        }
      };

      fetchData();
    }
  }, [projectId, proposalId]);

  const handleDropdownChange = (id, field, value) => {
    setRows(rows.map(row => {
      if (row.id === id) {
        const updatedRow = { ...row, [field]: value };
        // If set to No, clear the editable fields
        if (value === 'No') {
          updatedRow.kshetraNew = '';
          updatedRow.bhudharanBadalNew = '';
        }
        return updatedRow;
      }
      return row;
    }));
    // Reset edited fields tracking when dropdown changes
    setEditedFields(prev => ({ ...prev, [id]: [] }));
  };

  const handleInputChange = (id, field, value) => {
    setRows(rows.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    ));
    // Add the edited field to the array if not already present
    setEditedFields(prev => ({
      ...prev,
      [id]: prev[id] ? [...new Set([...prev[id], field])] : [field]
    }));
  };

  const handleSave = async (id) => {
    if (!projectId || !proposalId) {
      alert('Project or Proposal ID not found');
      return;
    }

    const rowToSave = rows.find(row => row.id === id);
    const fieldsToSave = editedFields[id] || [];

    // If no fields were edited, prevent save
    if (fieldsToSave.length === 0) {
      alert('No field edited');
      return;
    }

    setLoading(true);
    try {
      const measurementData = {};
      // Include only edited fields in the API call
      if (fieldsToSave.includes('kshetraNew')) {
        measurementData.owner_aqr_area = rowToSave.kshetraNew;
      }
      if (fieldsToSave.includes('bhudharanBadalNew')) {
        measurementData.owner_name = rowToSave.bhudharanBadalNew;
      }

      await apiService.patchUpdateMeasurement(id, measurementData);

      // Update the original fields with new values and set vargikaran to No
      setRows(rows.map(row => {
        if (row.id === id) {
          return {
            ...row,
            vargikaran: 'No',
            kshetra: fieldsToSave.includes('kshetraNew') ? rowToSave.kshetraNew : row.kshetra,
            bhudharanBadal: fieldsToSave.includes('bhudharanBadalNew') ? rowToSave.bhudharanBadalNew : row.bhudharanBadal,
            kshetraNew: '',
            bhudharanBadalNew: ''
          };
        }
        return row;
      }));

      // Clear edited fields tracking for this row
      setEditedFields(prev => ({ ...prev, [id]: [] }));

      alert('Row saved successfully');
    } catch (err) {
      console.error('Error saving row:', err);
      alert('Failed to save row');
    } finally {
      setLoading(false);
    }
  };

  const isEditable = (row) => {
    return row.vargikaran === 'Yes';
  };

  if (loading) {
    return <div>Loading data...</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-8">
      <div className="max-w-7xl mx-auto bg-white rounded-lg shadow-lg p-8">
        {/* Main Heading */}
        <div className="text-center mb-6">
          <h1 className="text-3xl font-bold text-indigo-900 mb-2">
            निवडा साडी अंतिम करणे
          </h1>
          <h2 className="text-xl font-semibold text-indigo-700 mb-4">
            क्षेत्र वगळणची कार्यवाही
          </h2>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full border-collapse border-2 border-indigo-300">
            <thead>
              <tr className="bg-indigo-600 text-white">
                <th className="border-2 border-indigo-400 px-4 py-3 text-left">
                  अ.क्र.
                </th>
                <th className="border-2 border-indigo-400 px-4 py-3 text-left">
                  गट नं
                </th>
                <th className="border-2 border-indigo-400 px-4 py-3 text-left">
                  वगळण
                </th>
                <th className="border-2 border-indigo-400 px-4 py-3 text-left">
                  क्षेत्र
                </th>
                <th className="border-2 border-indigo-400 px-4 py-3 text-left">
                  भूधाराकाचे नाव
                </th>
                <th className="border-2 border-indigo-400 px-4 py-3 text-center">
                  कृती
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, index) => (
                <tr key={row.id} className="hover:bg-indigo-50 transition-colors">
                  {/* Serial Number */}
                  <td className="border-2 border-indigo-200 px-4 py-3 font-semibold">
                    {index + 1}
                  </td>

                  {/* गट नं Input (surveyNo, disabled) */}
                  <td className="border-2 border-indigo-200 px-4 py-3">
                    <input
                      type="text"
                      value={row.surveyNo}
                      disabled
                      className="w-full px-3 py-2 cursor-not-allowed"
                      placeholder="गट नं"
                    />
                  </td>

                  {/* वर्गकन Dropdown */}
                  <td className="border-2 border-indigo-200 px-4 py-3">
                    <select
                      value={row.vargikaran}
                      onChange={(e) => handleDropdownChange(row.id, 'vargikaran', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-indigo-500"
                    >
                      <option value="No">No</option>
                      <option value="Yes">Yes</option>
                    </select>
                  </td>

                  {/* क्षेत्र Input Fields */}
                  <td className="border-2 border-indigo-200 px-4 py-3">
                    <input
                      type="text"
                      value={row.kshetra}
                      disabled
                      className="w-full px-3 py-2 mb-2 border border-gray-200 bg-gray-100 cursor-not-allowed text-black-400"
                      placeholder=""
                    />
                    {isEditable(row) && (
                      <input
                        type="text"
                        value={row.kshetraNew}
                        onChange={(e) => handleInputChange(row.id, 'kshetraNew', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-indigo-500"
                        placeholder="नवीन क्षेत्र प्रविष्ट करा"
                      />
                    )}
                  </td>

                  {/* भूधाराकाचे नाव Input Fields */}
                  <td className="border-2 border-indigo-200 px-4 py-3">
                    <input
                      type="text"
                      value={row.bhudharanBadal}
                      disabled
                      className="w-full px-3 py-2 mb-2 border border-gray-200 bg-gray-100 cursor-not-allowed text-black-400"
                      placeholder=""
                    />
                    {isEditable(row) && (
                      <input
                        type="text"
                        value={row.bhudharanBadalNew}
                        onChange={(e) => handleInputChange(row.id, 'bhudharanBadalNew', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:outline-none focus:border-indigo-500"
                        placeholder="नवीन भूधाराकाचे नाव प्रविष्ट करा"
                      />
                    )}
                  </td>

                  {/* Save Button */}
                  <td className="border-2 border-indigo-200 px-4 py-3 text-center">
                    <button
                      onClick={() => handleSave(row.id)}
                      disabled={loading || !isEditable(row) || !editedFields[row.id]?.length}
                      className={`px-3 py-1 rounded font-semibold transition-colors ${loading || !isEditable(row) || !editedFields[row.id]?.length
                          ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                          : 'bg-blue-500 hover:bg-blue-600 text-white'
                        }`}
                    >
                      {loading ? 'Saving...' : 'सेव्ह करा'}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default HighwaySection93;